import { useEffect, useState } from 'react';
import { CheckCircle2, ExternalLink, ShieldCheck, X } from 'lucide-react';

import { adbApi } from '@/lib/api';

interface CertPushResult {
  local: string;
  devicePath: string;
  bytes: number;
  opened: boolean;
}

/**
 * Modal flow for pushing a CA cert to the device.
 *
 * Why this is a modal and not just a button + spinner:
 *   - The flow is multi-step (pick file → push → open Settings → user
 *     navigates the device UI to actually install).
 *   - Android only lets adb `push` the file; the install step requires
 *     human input on the device (PIN, name the cert, etc). The modal stays
 *     open with the path + guidance text so the user can refer back to it.
 *
 * Browser file inputs only expose the basename + bytes, NOT the absolute
 * filesystem path the server needs for `fs.push`. So the user pastes (or
 * drag-drops to populate) an absolute path. We surface common Charles /
 * mitmproxy default locations as `<datalist>` presets to lower the friction.
 */
const CERT_PATH_PRESETS = [
  // macOS common Charles location
  '~/Library/Application Support/Charles/charles-ssl-proxying-certificate.pem',
  // mitmproxy default
  '~/.mitmproxy/mitmproxy-ca-cert.pem',
  '~/.mitmproxy/mitmproxy-ca-cert.cer',
];

export function CertPushModal({
  serial,
  onClose,
}: {
  serial?: string;
  onClose: () => void;
}) {
  const [localPath, setLocalPath] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CertPushResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [openSettings, setOpenSettings] = useState(true);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (): Promise<void> => {
    if (!serial) return;
    if (!localPath.trim()) {
      setErr('请粘贴本地证书的绝对路径');
      return;
    }
    setBusy(true);
    setErr(null);
    setResult(null);
    try {
      const { result: r } = await adbApi.run<CertPushResult>({
        serial,
        name: 'net.cert.push',
        args: { localPath: localPath.trim(), openSettings },
      });
      setResult(r);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-xl p-5"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="推送 CA 证书"
      >
        <header className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-500" />
            <h2 className="text-base font-semibold text-ink">推送 CA 证书到设备</h2>
          </div>
          <button className="btn-ghost" onClick={onClose} title="关闭">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="mt-3 space-y-3 text-sm text-ink">
          <p className="text-ink-muted">
            把本地的 Charles / mitmproxy / Whistle 根证书 (
            <code className="font-mono">.crt</code> /<code className="font-mono">.pem</code>) 推到{' '}
            <code className="font-mono">/sdcard/Download/adh-&lt;name&gt;</code>，再跳转到系统的"安全设置"。
          </p>

          <div>
            <label className="text-[11px] uppercase tracking-wide text-ink-muted">
              本地路径（绝对路径）
            </label>
            <input
              className="input mt-1 w-full font-mono text-[12px]"
              list="cert-presets"
              placeholder="/Users/me/Library/Application Support/Charles/charles-ssl-proxying-certificate.pem"
              value={localPath}
              onChange={(e) => setLocalPath(e.target.value)}
              disabled={busy}
              autoFocus
            />
            <datalist id="cert-presets">
              {CERT_PATH_PRESETS.map((p) => (
                <option key={p} value={p} />
              ))}
            </datalist>
            <p className="mt-1 text-[11px] text-ink-muted">
              支持 <code className="font-mono">~</code> 开头，例如{' '}
              <code className="font-mono">~/.mitmproxy/mitmproxy-ca-cert.pem</code>。
            </p>
          </div>

          <label className="flex items-center gap-2 text-[12px] text-ink-muted">
            <input
              type="checkbox"
              checked={openSettings}
              onChange={(e) => setOpenSettings(e.target.checked)}
              disabled={busy}
            />
            推送后自动打开"设置 → 安全"，方便用户去手动安装。
          </label>

          {err && (
            <p className="rounded-md border border-rose-500/40 bg-rose-500/5 px-3 py-2 text-[12px] text-rose-500">
              {err}
            </p>
          )}

          {result && (
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-[12px] text-ink">
              <div className="flex items-center gap-1.5 font-medium text-emerald-600">
                <CheckCircle2 className="h-3.5 w-3.5" />
                推送完成 · {result.bytes} 字节
              </div>
              <div className="mt-1 break-all font-mono text-[11px] text-ink-muted">
                设备路径：{result.devicePath}
              </div>
              <ol className="mt-2 list-decimal pl-5 leading-relaxed text-ink-muted">
                <li>
                  在设备上打开{' '}
                  <span className="font-medium text-ink">
                    设置 → 安全 → 加密与凭据 → 安装证书 → CA 证书
                  </span>
                  {result.opened ? '（已自动跳转）' : '（设备未自动跳转，请手动打开）'}。
                </li>
                <li>
                  选择刚推送的文件 <code className="font-mono">{result.devicePath}</code>，按提示完成安装。
                </li>
                <li>
                  Android 11+ 用户安装的 CA 仅对设置了
                  <code className="font-mono"> network_security_config</code> 的 App 生效；
                  系统应用 / 第三方应用默认仍然不受信任。这是 Android 安全策略，adh 不会绕过。
                </li>
              </ol>
            </div>
          )}
        </div>

        <footer className="mt-5 flex items-center justify-between">
          <a
            href="https://developer.android.com/training/articles/security-config"
            target="_blank"
            rel="noreferrer"
            className="btn-ghost text-[12px]"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            为什么需要 networkSecurityConfig?
          </a>
          <div className="flex items-center gap-2">
            <button className="btn" onClick={onClose} disabled={busy}>
              {result ? '完成' : '取消'}
            </button>
            <button className="btn-primary" onClick={submit} disabled={!serial || busy}>
              {busy ? '推送中...' : '推送'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
